import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Client-side document-head manager for per-route SEO.
 *
 * Replaces react-helmet-async, which does not update the head reliably in this
 * stack. This writes directly to document.head: it upserts the title, meta, and
 * canonical tags (updating the static tags already in index.html rather than
 * duplicating them) and swaps the route's JSON-LD on every navigation.
 */
function upsertMeta(key: string, keyType: "name" | "property", content: string) {
  const selector = `meta[${keyType}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyType, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const Seo = ({ title, description, path, ogType = "website", ogImage, jsonLd }: SeoProps) => {
  const url = `${siteConfig.url}${path}`;
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${siteConfig.url}${ogImage}`
    : `${siteConfig.url}${siteConfig.ogImage}`;

  useEffect(() => {
    document.title = title;
    upsertMeta("description", "name", description);
    upsertLink("canonical", url);

    upsertMeta("og:type", "property", ogType);
    upsertMeta("og:title", "property", title);
    upsertMeta("og:description", "property", description);
    upsertMeta("og:url", "property", url);
    upsertMeta("og:image", "property", image);

    upsertMeta("twitter:title", "name", title);
    upsertMeta("twitter:description", "name", description);
    upsertMeta("twitter:image", "name", image);

    const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    document.head.querySelectorAll('script[data-seo-ld="1"]').forEach((s) => s.remove());
    for (const ld of ldArray) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-ld", "1");
      s.textContent = JSON.stringify(ld);
      document.head.appendChild(s);
    }
  }, [title, description, url, ogType, image, jsonLd]);

  return null;
};

export default Seo;
